/**
 * Carrier MCP stdio — ui_agent_ask + ui_agent_status, live.
 *
 * These two are the browser-agent tools that CAN run in a CLI, and as of this
 * change they do. They drive a hosted agent over the open web, so unlike the
 * ten `ui_*` OCS-dashboard tools they need no OCS portal credentials, and
 * unlike `ui_agent_schedule_*` they need no recurring runtime. What they need
 * is an API key and somewhere to keep a task record, and both now exist:
 * `BROWSER_USE_API_KEY` from `process.env` and the Map-backed store in
 * `ui-agent-runtime.ts`.
 *
 * The client itself is `@carrier/ai`, shared with the Worker. It is not copied
 * here — that is the whole point of the extraction that preceded this.
 *
 * COST
 * ----
 * A run costs roughly $0.20-1.00 of real money against the user's own key. The
 * Worker bounds this with `reserveAiCalls` against KV; stdio has no KV, so
 * dispatch goes through `RunBudget` instead — a per-process ceiling, default 10,
 * settable with `CARRIER_BROWSER_USE_MAX_RUNS`. See `ui-agent-runtime.ts` for
 * what that does and does not prevent.
 *
 * DISPATCH IS SYNCHRONOUS HERE, AND THAT IS A REAL DIFFERENCE
 * ----------------------------------------------------------
 * The Worker returns a task_id immediately and finishes the run in a Durable
 * Object, so `ui_agent_status` polls something that keeps running after the
 * call returns. A CLI has no such background: nothing survives the tool call.
 * `ui_agent_ask` therefore awaits the run and returns the result, and
 * `ui_agent_status` reads the finished record back out of the session store.
 * The tool contract is unchanged — a caller that dispatches then polls still
 * works — but the first call blocks for the length of the run rather than
 * returning in milliseconds.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import type { ToolScope } from "./types.js";
import {
  browserUseConfigured,
  getUiAgentTask,
  runUiAgent,
  type SteelTask,
} from "@carrier/ai";
import {
  SpendLimitError,
  type StdioUiAgentRuntime,
} from "./ui-agent-runtime.js";
import type { ToolResult } from "./ui-agent-stub.js";

/** Scope + registry exports, mirroring the Worker's registry for parity. */
export const UI_AGENT_GENERIC_TOOL_SCOPES: Record<string, ToolScope> = {
  ui_agent_ask: "write",
  ui_agent_status: "read",
};

/**
 * Pre-flight guess only, and a worse guess than it once was — the hosted agent
 * runs its own planner and takes no step cap, so `max_steps` is advisory. Kept
 * because a dry run has nothing better to offer; a finished run reports the
 * vendor's real billed figure instead.
 */
const COST_PER_STEP_USD = 0.014;
const SESSION_OVERHEAD_USD = 0.005;

function estimateCostUsd(steps: number): number {
  return (
    Math.round((COST_PER_STEP_USD * steps + SESSION_OVERHEAD_USD) * 100) / 100
  );
}

function notConfiguredError(toolName: string): ToolResult {
  return {
    isError: true,
    content: [
      {
        type: "text" as const,
        text: JSON.stringify({
          error: "browser_use_not_configured",
          tool: toolName,
          message:
            "BROWSER_USE_API_KEY is not set, so the browsing agent cannot run. " +
            "Set it in the environment of the process running this MCP server. " +
            "Runs are billed to that key at roughly $0.20-1.00 each.",
        }),
      },
    ],
  };
}

function scopeError(
  toolName: string,
  required: string,
  actual: string[],
): ToolResult {
  return {
    isError: true,
    content: [
      {
        type: "text" as const,
        text: JSON.stringify({
          error: "scope_denied",
          message:
            `Scope denied: tool '${toolName}' requires '${required}' scope. ` +
            `Your token has: [${actual.join(", ")}].`,
        }),
      },
    ],
  };
}

export function registerUiAgentGenericTools(
  server: McpServer,
  ctx: ToolContext,
  runtime: StdioUiAgentRuntime,
): void {
  // =========================================================================
  // ui_agent_ask — dispatch a generic browsing agent
  // =========================================================================
  server.registerTool(
    "ui_agent_ask",
    {
      title: "Dispatch Generic Browsing Agent",
      description:
        "Send a plain-language task to a hosted browsing agent, which drives a real browser on the open web — " +
        "navigating pages, searching, extracting content, filling forms outside the OCS portal. " +
        "Each run costs roughly $0.20-1.00 in model spend against your own BROWSER_USE_API_KEY, so treat it as " +
        "the expensive last resort, not a lookup. Limited to " +
        "CARRIER_BROWSER_USE_MAX_RUNS runs per CLI session (default 10). " +
        "Params: `prompt` (1-4000 chars — name the target URL and the exact data or action), " +
        "`max_steps` (1-30, default 15; prices the dry-run estimate only), " +
        "`dry_run` (true returns only `estimated_cost_usd` and creates no session). " +
        "Returns the finished result: unlike the hosted Worker, a local CLI has no background runtime, " +
        "so this call blocks for the length of the run. Requires `write` scope. " +
        "Do NOT use this to work out which Carrier tool answers a request — that is `carrier_ask`, which costs nothing. " +
        "Do NOT use this for anything the OCS API already covers; every other tool here is faster, free, and audited.",
      inputSchema: {
        prompt: z
          .string()
          .min(1)
          .max(4000)
          .describe(
            "Natural-language task description for the browsing agent. " +
              "Be specific: include target URLs, data to extract, or actions to perform.",
          ),
        max_steps: z
          .number()
          .int()
          .min(1)
          .max(30)
          .optional()
          .describe(
            "Advisory step budget (1–30, default 15). Prices the dry-run estimate; the hosted agent sets its own depth.",
          ),
        dry_run: z
          .boolean()
          .optional()
          .describe(
            "Preview cost estimate without dispatching. Returns estimated_cost_usd only — no browser session is created.",
          ),
      },
    },
    async (args: {
      prompt: string;
      max_steps?: number;
      dry_run?: boolean;
    }): Promise<ToolResult> => {
      const start = Date.now();
      const toolName = "ui_agent_ask";
      const { prompt, max_steps = 15, dry_run = false } = args;

      if (
        !ctx.props.scope.includes("write") &&
        !ctx.props.scope.includes("admin")
      ) {
        ctx.audit({
          tool_name: toolName,
          ocs_method: "[ui-agent:generic-ask]",
          status: "scope_denied",
          dry_run: false,
          duration_ms: 0,
        });
        return scopeError(toolName, "write", ctx.props.scope);
      }

      // Checked before the budget is consumed: a dry run spends nothing, so it
      // must not burn one of the session's allowance.
      if (dry_run) {
        return {
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(
                {
                  status: "dry_run",
                  estimated_cost_usd: estimateCostUsd(max_steps),
                  runs_remaining_this_session: runtime.budget.remaining,
                  note: "No browser session was created and nothing was billed.",
                },
                null,
                2,
              ),
            },
          ],
        };
      }

      if (!browserUseConfigured(runtime.env)) {
        return notConfiguredError(toolName);
      }

      try {
        runtime.budget.reserve();
      } catch (err) {
        if (err instanceof SpendLimitError) {
          ctx.audit({
            tool_name: toolName,
            ocs_method: "[ui-agent:generic-ask]",
            // The existing quota status, not a new one: AuditRow's status is a
            // closed union shared with the Worker, and a stdio-only value would
            // be a row shape the hosted surface never emits.
            status: "quota_exceeded",
            dry_run: false,
            duration_ms: 0,
            event_type: "ui_agent_dispatch",
          });
          return {
            isError: true,
            content: [
              {
                type: "text" as const,
                text: JSON.stringify({
                  error: "run_limit_reached",
                  message: err.message,
                }),
              },
            ],
          };
        }
        throw err;
      }

      try {
        const result = await runUiAgent(
          prompt,
          "Carrier CLI browsing agent",
          runtime.env,
        );

        // Recorded so ui_agent_list_pending has something to enumerate — see
        // StdioUiAgentRuntime.dispatchedTaskIds for why the store has no list().
        runtime.dispatchedTaskIds.add(result.task_id);

        ctx.audit({
          tool_name: toolName,
          ocs_method: "[ui-agent:generic-ask]",
          status: result.status === "error" ? "error" : "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          event_type: "ui_agent_dispatch",
        });

        return {
          ...(result.status === "error" ? { isError: true } : {}),
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(
                {
                  status: result.status,
                  task_id: result.task_id,
                  ...(result.session_viewer_url
                    ? { session_viewer_url: result.session_viewer_url }
                    : {}),
                  result: result.result,
                  session_spend_usd: runtime.spend.costUsd,
                  runs_remaining_this_session: runtime.budget.remaining,
                  note:
                    "The run completed in-process. Task records live only for this " +
                    "CLI session — ui_agent_status can read this task_id until the process exits.",
                },
                null,
                2,
              ),
            },
          ],
        };
      } catch (err) {
        ctx.audit({
          tool_name: toolName,
          ocs_method: "[ui-agent:generic-ask]",
          status: "error",
          dry_run: false,
          duration_ms: Date.now() - start,
          event_type: "ui_agent_dispatch",
        });
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: JSON.stringify({
                error: "ui_agent_failed",
                message: err instanceof Error ? err.message : String(err),
              }),
            },
          ],
        };
      }
    },
  );

  // =========================================================================
  // ui_agent_status — read a task record back
  // =========================================================================
  server.registerTool(
    "ui_agent_status",
    {
      title: "Poll Browsing Agent Task Status",
      description:
        "Returns the current state of a browsing-agent task by `task_id`, including its result once finished. " +
        "In the local CLI, ui_agent_ask already returns the finished result, so this is mainly for re-reading a " +
        "task within the same session. Task records do not survive the CLI process exiting. " +
        "Requires `read` scope.",
      inputSchema: {
        task_id: z
          .string()
          .describe("Task ID returned by ui_agent_ask"),
      },
    },
    async (args: { task_id: string }): Promise<ToolResult> => {
      const toolName = "ui_agent_status";

      if (ctx.props.scope.length === 0) {
        return scopeError(toolName, "read", ctx.props.scope);
      }

      if (!browserUseConfigured(runtime.env)) {
        return notConfiguredError(toolName);
      }

      const task: SteelTask | null = await getUiAgentTask(
        args.task_id,
        runtime.env,
      );

      if (!task) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: JSON.stringify({
                error: "task_not_found",
                message:
                  `No task record for task_id=${args.task_id}. ` +
                  "In the local CLI these live in memory for the lifetime of the process, " +
                  "so a task from an earlier session is gone rather than expired.",
              }),
            },
          ],
        };
      }

      return {
        content: [
          {
            type: "text" as const,
            text: JSON.stringify(task, null, 2),
          },
        ],
      };
    },
  );
}
